/**
 * ============================================================================
 * Web 前端监控日志模块路由定义
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Web 前端监控日志模块的所有 API 路由常量
 * - 从 v1.ts 路由表中提取相关路由
 * - 提供类型安全的路由访问
 * 
 * 📌 模块说明：
 * 本模块用于 **Web 管理后台前端** 的错误和性能日志。
 * 
 * ⚠️ 注意区分：
 * - 本模块 (web-logs)：Web 管理后台前端的日志（浏览器端）
 *   API 路径：/api/v1/web-logs/
 *   
 * - app-logs 模块：移动 App 端的日志（iOS/Android/小程序）
 *   API 路径：/api/v1/app-logs/
 * 
 * @module api/web-logs/routes
 * @generated Manual (AI-assisted)
 * @author YSFL Team
 * @created 2026-01-24
 * @updated 2026-01-24 路径重命名 /web/ → /web-logs/
 * ============================================================================
 */

import { API_V1_ROUTES } from '../routes/v1'

/**
 * Web 前端监控日志模块路由定义
 * 
 * 使用示例：
 * ```typescript
 * import { WEB_LOGS_ROUTES } from '@bayes-mes/shared/api/web-logs'
 * 
 * // 获取完整路径
 * const path = WEB_LOGS_ROUTES.errors.batch  // '/api/v1/web-logs/errors'
 * ```
 */
export const WEB_LOGS_ROUTES = {
  // =========================================================================
  // 仪表盘
  // =========================================================================
  /** 仪表盘统计 GET /api/v1/web-logs/dashboard */
  dashboard: API_V1_ROUTES.webLogs.dashboard,

  // =========================================================================
  // Web 前端错误日志
  // =========================================================================
  errors: {
    /** 批量上传 Web 前端错误日志 POST /api/v1/web-logs/errors */
    batch: API_V1_ROUTES.webLogs.errors,
    /** Web 前端错误日志列表（分页）GET /api/v1/web-logs/errors */
    list: API_V1_ROUTES.webLogs.errors,
    /** Web 前端错误统计 GET /api/v1/web-logs/errors/statistics */
    statistics: API_V1_ROUTES.webLogs.errorsStatistics,
  },

  // =========================================================================
  // Web 前端性能日志
  // =========================================================================
  performance: {
    /** 批量上传 Web 前端性能日志 POST /api/v1/web-logs/performance */
    batch: API_V1_ROUTES.webLogs.performance,
    /** Web 前端性能日志列表（分页）GET /api/v1/web-logs/performance */
    list: API_V1_ROUTES.webLogs.performance,
    /** Web 前端性能统计 GET /api/v1/web-logs/performance/statistics */
    statistics: API_V1_ROUTES.webLogs.performanceStatistics,
  },

  // =========================================================================
  // 用户分析
  // =========================================================================
  users: {
    /** 用户列表 GET /api/v1/web-logs/users */
    list: API_V1_ROUTES.webLogs.users,
    /** 用户画像 GET /api/v1/web-logs/users/{userId}/profile */
    profile: API_V1_ROUTES.webLogs.userProfile,
    /** 用户时间线 GET /api/v1/web-logs/users/{userId}/timeline */
    timeline: API_V1_ROUTES.webLogs.userTimeline,
  },

  // =========================================================================
  // API 分析
  // =========================================================================
  api: {
    /** API 统计 GET /api/v1/web-logs/api/statistics */
    statistics: API_V1_ROUTES.webLogs.apiStatistics,
    /** API 详情 GET /api/v1/web-logs/api/detail */
    detail: API_V1_ROUTES.webLogs.apiDetail,
  },

  // =========================================================================
  // 告警管理
  // =========================================================================
  alerts: {
    /** 检测告警 POST /api/v1/web-logs/alerts/detect */
    detect: API_V1_ROUTES.webLogs.alertsDetect,
    /** 告警汇总 GET /api/v1/web-logs/alerts/summary */
    summary: API_V1_ROUTES.webLogs.alertsSummary,
  },

  // =========================================================================
  // 行为日志分析
  // =========================================================================
  behavior: {
    /** 批量上传/列表查询行为日志 POST/GET /api/v1/web-logs/behavior */
    batch: API_V1_ROUTES.webLogs.behavior,
    /** 行为日志列表 GET /api/v1/web-logs/behavior */
    list: API_V1_ROUTES.webLogs.behavior,
    /** 行为统计 GET /api/v1/web-logs/behavior/statistics */
    statistics: API_V1_ROUTES.webLogs.behaviorStatistics,
    /** 热门页面 GET /api/v1/web-logs/behavior/hot-pages */
    hotPages: API_V1_ROUTES.webLogs.behaviorHotPages,
    /** 模块使用统计 GET /api/v1/web-logs/behavior/modules */
    modules: API_V1_ROUTES.webLogs.behaviorModules,
  },

  // =========================================================================
  // 漏斗分析
  // =========================================================================
  funnel: {
    /** 漏斗定义列表 GET /api/v1/web-logs/funnel/list */
    list: API_V1_ROUTES.webLogs.funnelList,
    /** 漏斗转化分析 GET /api/v1/web-logs/funnel/analysis */
    analysis: API_V1_ROUTES.webLogs.funnelAnalysis,
    /** 操作中断分析 GET /api/v1/web-logs/funnel/interruptions */
    interruptions: API_V1_ROUTES.webLogs.funnelInterruptions,
    /** 创建漏斗定义 POST /api/v1/web-logs/funnel */
    create: API_V1_ROUTES.webLogs.funnel,
    /** 更新漏斗定义 PUT /api/v1/web-logs/funnel/{id} */
    update: API_V1_ROUTES.webLogs.funnelById,
    /** 删除漏斗定义 DELETE /api/v1/web-logs/funnel/{id} */
    remove: API_V1_ROUTES.webLogs.funnelById,
  },
} as const

/** 路由类型导出 */
export type WebLogsRoutes = typeof WEB_LOGS_ROUTES
